/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {Component, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {NavigationEnd, Route, Router} from '@angular/router';
import {ConfirmationService} from 'primeng/api';
import {HelpInfo} from '../../model/helpInfo';
import {ResponseWrapper} from '../../model/responseWrapper';
import {EsciMessageService} from '../../shared/esci-message.service';
import {HelpInfoService} from '../../service/help-info/help-info.service';
import {UserService} from '../../shared/user.service';

@Component({
    selector: 'help-info',
    templateUrl: './help-info.component.html',
    styleUrls: ['./help-info.component.less'],
    standalone: false
})
export class HelpInfoComponent implements OnInit {

  helpMessage: string;
  editEnabled = false;
  loading = true;
  possiblePathVarRoutes: string[] = [];
  workingRoutePath: string;

  helpInfoForm: UntypedFormGroup;

  constructor(private router: Router,
              private userService: UserService,
              private confirmationService: ConfirmationService,
              private helpInfoService: HelpInfoService,
              private messageService: EsciMessageService,
              private formBuilder: UntypedFormBuilder) {

    this.helpInfoForm = this.formBuilder.group({
      helpInfoEditor: [''],
    });

  }

  ngOnInit(): void {

    // Generate all possible paths for a help information and load the array
    this.generateRoutePaths(this.possiblePathVarRoutes, '', this.router.config);

    this.showHelp(this.router.url);
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {

        const newPath = this.getHelpPath(event.url);
        if (this.editEnabled && this.workingRoutePath !== newPath) {
          this.confirmationService.confirm({
            header: 'Save Help Information',
            message: 'Do you want to save the help information?',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
              this.saveHelpInfo();
              this.toggleEditor();
              this.showHelp(event.url);
            },
            reject: () => {
              this.toggleEditor();
              this.showHelp(event.url);
            }
          });
        } else {
          this.showHelp(event.url);
        }
      }
    });
  }

  /**
   * This method will generate all the possible path for help information
   *  it will drop the paths that have children and the one with a path variable
   * @param pathList The array holding the current list of paths
   * @param parent The parent route to look for children
   * @param config The children of the parent
   */
  generateRoutePaths(pathList: string[], parent: string, config: Route[]) {

    for (const route of config) {

      if (!route.path.includes(':')
        && !route.path.includes('auth-callback')
        && !route.path.includes('**')) {

        if (route.children) {
          const currentPath = route.path ? parent + '/' + route.path : parent;
          this.generateRoutePaths(pathList, currentPath, route.children);
        } else {

          // We want to remove the high level '/' path from the available options
          //  because this is part of all the possible routes.  Our goal was to find the correct
          //  route for paths with path variable.   Just a '/' will never been a variable route
          const possiblePath = parent + '/' + route.path;
          if (possiblePath !== '/') {
            pathList.push(parent + '/' + route.path);
          }
        }
      }
    }
  }

  /**
   * Search the possible paths for help information
   *   This is an attempt to remove the path variables and get the real help info path
   * @param routeUrlPath The path to search
   */
  getHelpPath(routeUrlPath: string): string {

    for (const possibleRoute of this.possiblePathVarRoutes) {
      if (routeUrlPath.startsWith(possibleRoute)) {
        return possibleRoute;
      }
    }
    return routeUrlPath;
  }



  showHelp(pathString: string) {

    this.loading = true;
    this.workingRoutePath = this.getHelpPath(pathString);

    // Call the service to get the HelpInfo by the routeKey
    this.helpInfoService.getHelpInfoByRoute(this.workingRoutePath).subscribe(({

      next: (res) => {
        const response = Object.assign(new ResponseWrapper(), res);
        if (response.success === true) {

          // Only expect one HelpInfo object in the data
          const helpInfoMO: HelpInfo = Object.assign(new HelpInfo(), response.data[0]);
          this.helpMessage = helpInfoMO.helpInfoStr;
          this.helpInfoForm.get('helpInfoEditor').setValue(this.helpMessage);

        } else {
          const error = new Error;
          error.message = response.errorMessages;
          this.messageService.processException(error);
        }

        this.loading = false;
      },
      error: (error) => {
        this.messageService.processException(error);
        this.loading = false;
      }
    }));

  }

  saveHelpInfo() {

    const helpInfoDto: HelpInfo = new HelpInfo();
    helpInfoDto.routeKey = this.workingRoutePath;
    helpInfoDto.helpInfoStr = this.helpInfoForm.get('helpInfoEditor').value;

    this.helpInfoService.saveHelpInfo(helpInfoDto).subscribe(({

      next: (res) => {
        const response = Object.assign(new ResponseWrapper(), res);
        if (response.success === true) {
          this.messageService.processSuccess('Help Information Updated Successfully');
          this.helpMessage = this.helpInfoForm.get('helpInfoEditor').value;
        } else {
          this.messageService.processResponseWrapperErrorMessage(response);
        }
      },
      error: (error) => {
        this.messageService.processException(error);
      }

    }));
  }

  toggleEditor() {
    this.editEnabled = !this.editEnabled;

    if (this.editEnabled) {
      this.helpInfoForm.get('helpInfoEditor').setValue(this.helpMessage);
    } else {

      const newHelpInfo = this.helpInfoForm.get('helpInfoEditor').value;
      if (newHelpInfo !== this.helpMessage) {
        this.confirmationService.confirm({
          header: 'Save Help Information',
          message: 'Changes detected in the help information. Do you want to save?',
          icon: 'pi pi-exclamation-triangle',
          accept: () => {
            this.saveHelpInfo();
          },
          reject: () => {
          }
        });
      } else {
        this.helpMessage = this.helpInfoForm.get('helpInfoEditor').value;
      }
    }
  }

  allowedToEditHelpInfo() {
    const role = ['PERM_EDIT_HELPINFO'];
    const userDetailsObject = this.userService.user;

    if (userDetailsObject) {
      return userDetailsObject.checkRoles(role, false);
    } else {
      return false;
    }
  }
}
